import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Facebook, Link2, MessageCircle, PlayCircle, Send } from "lucide-react";
import { PageShell, VideoPlayer, mediaUrl } from "../components/legend/PageShell";
import { useInterviste } from "../components/legend/NewsTicker";
import { IMAGES, SOCIAL } from "../data/content";
import { videoThumb } from "./Gare";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

const Avatar = () => (
  <span className="flex items-center gap-3">
    <span className="w-10 h-10 rounded-full bg-black border-2 border-red-600 flex items-center justify-center overflow-hidden">
      <img src={IMAGES.logoWhite} alt="Legend Team" className="w-8 object-contain" />
    </span>
    <span className="leading-tight">
      <span className="block text-sm font-black text-white">legendteam_asd</span>
      <span className="block mono-label text-[10px] uppercase text-neutral-500">Video interviste &bull; Legend Magazine</span>
    </span>
  </span>
);

const cover = (it) => mediaUrl(it.copertina) || videoThumb(it.video) || IMAGES.heroCar;

/** Feed principale stile social: post video in ordine di pubblicazione. */
export default function Interviste() {
  const items = useInterviste();
  return (
    <PageShell testid="interviste-page" eyebrow="Legend Team Social" title="Video interviste" subtitle="Piloti, navigatori, partner e protagonisti del Legend Team raccontati dalla redazione di Legend Magazine. Tocca un post per aprire l'intervista completa.">
      <div className="max-w-2xl mx-auto px-4 space-y-8">
        {items.map((it) => (
          <article key={it.id} data-testid={`post-${it.slug}`} className="border border-neutral-800 bg-neutral-900">
            <header className="flex items-center justify-between p-4"><Avatar /><span className="mono-label text-[10px] text-neutral-500 uppercase">{fmt(it.created_at)}</span></header>
            <Link to={`/interviste/${it.slug}`} className="group relative block aspect-video bg-black overflow-hidden">
              <img src={cover(it)} alt={it.titolo} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-opacity" loading="lazy" />
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <PlayCircle className="w-16 h-16 text-white drop-shadow-lg group-hover:scale-110 transition-transform" />
                {it.video?.tipo === "instagram" && <span className="mono-label text-[10px] uppercase tracking-widest bg-black/70 text-white px-2 py-1">Reel Instagram</span>}
              </span>
            </Link>
            <div className="p-4 space-y-2">
              <div className="flex items-center gap-4 text-neutral-300">
                <Link to={`/interviste/${it.slug}`} className="inline-flex items-center gap-1.5 text-xs hover:text-red-500"><MessageCircle className="w-5 h-5" /> Leggi l'articolo</Link>
                <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs hover:text-red-500"><Send className="w-5 h-5" /> Instagram</a>
              </div>
              <p className="text-sm text-neutral-200"><span className="font-black text-white mr-2">legendteam_asd</span>{it.titolo}</p>
              {it.didascalia && <p className="text-sm text-neutral-400">{it.didascalia}</p>}
              <Link to={`/interviste/${it.slug}`} className="block mono-label text-[11px] uppercase tracking-wider text-red-500 hover:text-red-400">Apri l'intervista &rarr;</Link>
            </div>
          </article>
        ))}
        {items.length === 0 && <p className="text-center text-neutral-500 text-sm">Nessuna intervista pubblicata.</p>}
      </div>
    </PageShell>
  );
}

const ShareBar = ({ title }) => {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const copy = () => navigator.clipboard?.writeText(url).then(() => toast.success("Link copiato"));
  const btn = "inline-flex items-center gap-2 px-4 py-2 border border-neutral-700 hover:border-red-600 text-neutral-200 hover:text-red-500 mono-label text-[11px] uppercase tracking-wider transition-colors";
  return (
    <div data-testid="share-bar" className="flex flex-wrap gap-2">
      <a className={btn} href={`https://wa.me/?text=${encodeURIComponent(`${title} — ${url}`)}`} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
      <a className={btn} href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer"><Facebook className="w-4 h-4" /> Facebook</a>
      <a className={btn} href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`} target="_blank" rel="noopener noreferrer"><Send className="w-4 h-4" /> Telegram</a>
      <button type="button" data-testid="share-copy" className={btn} onClick={copy}><Link2 className="w-4 h-4" /> Copia link</button>
    </div>
  );
};

export function IntervistaDettaglio() {
  const { slug } = useParams();
  const [it, setIt] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    axios.get(`${API}/interviste/${slug}`).then((r) => setIt(r.data)).catch(() => setErr(true));
  }, [slug]);
  if (err) return <PageShell testid="intervista-404" title="Intervista non trovata" back="/interviste" />;
  if (!it) return <PageShell testid="intervista-loading" title="Caricamento..." back="/interviste" />;
  return (
    <PageShell testid="intervista-page" eyebrow={`Video intervista • ${fmt(it.created_at)}`} title={it.titolo} subtitle={it.didascalia} back="/interviste">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-8">
        <div className="border border-neutral-800">
          {it.video ? <VideoPlayer media={it.video} /> : (
            <div data-testid="video-placeholder" className="aspect-video bg-neutral-900 flex items-center justify-center text-neutral-500 text-sm">Video in arrivo</div>
          )}
        </div>
        <article data-testid="intervista-articolo" className="border border-neutral-800 bg-neutral-900 p-6 sm:p-8">
          <div className="flex items-center justify-between gap-4 mb-6 pb-4 border-b border-neutral-800">
            <Avatar />
            <img src={IMAGES.logoMagazine} alt="Legend Magazine" className="h-8 bg-white px-2 py-1" />
          </div>
          <div className="text-sm sm:text-base text-neutral-200 leading-relaxed whitespace-pre-line">{it.articolo}</div>
          <p className="mt-8 mono-label text-xs uppercase tracking-wider text-red-500">— {it.autore}</p>
        </article>
        <ShareBar title={it.titolo} />
      </div>
    </PageShell>
  );
}
